import React from 'react';

// Section wrapper with grain texture
export const Section = ({ id, className = '', children, dark = false }) => (
  <section
    id={id}
    className={`relative grain ${dark ? 'bg-stone-800 text-white' : ''} ${className}`}
  >
    {children}
  </section>
);

// Container for max-width content
export const Container = ({ className = '', children }) => (
  <div className={`max-w-6xl mx-auto px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

// Section header with tag and title
export const SectionHeader = ({ tag, title, titleEmphasis, centered = false }) => (
  <div className={centered ? 'text-center mb-20' : 'mb-8'}>
    <p className="section-tag">{tag}</p>
    <h2 className="section-title">
      {title}
      {titleEmphasis && (
        <span className="italic text-amber-800"> {titleEmphasis}</span>
      )}
    </h2>
  </div>
);

// Service card
export const ServiceCard = ({ title, description, features }) => (
  <div className="card group">
    <div className="w-12 h-px bg-amber-300 mb-8 group-hover:w-20 transition-all duration-500" />
    <h3 className="font-display text-2xl text-stone-800 mb-4">{title}</h3>
    <p className="font-body text-stone-600 leading-relaxed mb-6">{description}</p>
    <ul className="space-y-2">
      {features.map((feature, i) => (
        <li key={i}>
          <div className="font-body text-sm text-stone-700 flex items-start gap-3">
            <span className="w-1.5 h-1.5 bg-amber-300 rounded-full mt-2" />
            <span>{feature.activity}</span>
          </div>
          {(feature.details || feature.courseLink) && (
            <p className="font-body text-sm text-stone-500 ml-6 mt-1">
              {feature.details}
              {feature.courseLink && (
                <a
                  href={feature.courseLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-700 hover:text-amber-800 underline ml-2"
                >
                  Course Link →
                </a>
              )}
            </p>
          )}
        </li>
      ))}
    </ul>
  </div>
);

// Credential card
export const CredentialCard = ({ type, details }) => (
  <div className="bg-white p-8 shadow-sm">
    <p className="font-body text-xs tracking-widest uppercase text-amber-700 mb-3">{type}</p>
    <ul className="space-y-2">
      {details.map((detail, i) => (
        <li key={i}>
          <div className="font-body text-sm text-stone-700 flex items-start gap-3">
            <span className="w-1.5 h-1.5 bg-amber-300 rounded-full mt-2" />
            <div>
              <p className="font-body text-sm text-stone-700">{detail.degree}</p>
              <p className="font-body text-xs text-stone-500">{detail.institution}</p>
            </div>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

// Timeline item
export const TimelineItem = ({ role, org, desc }) => (
  <div className="flex gap-8 items-start group">
    <div className="w-4 h-4 bg-amber-200 rounded-full mt-2 group-hover:bg-amber-400 transition-colors flex-shrink-0" />
    <div className="flex-1 border-b border-stone-100 pb-8">
      <h3 className="font-display text-xl text-stone-800">{role}</h3>
      <p className="font-body text-amber-700">{org}</p>
      <p className="font-body text-stone-600 mt-3">{desc}</p>
    </div>
  </div>
);

// Quote block
export const QuoteBlock = ({ text, author, variant = 'light' }) => (
  <div className={`py-24 relative grain ${variant === 'light' ? 'bg-amber-50' : 'bg-stone-800'}`}>
    <Container className="text-center">
      <div className={`font-display text-6xl mb-6 ${variant === 'light' ? 'text-amber-300' : 'text-amber-400'}`}>
        "
      </div>
      <blockquote
        className={`font-display text-2xl md:text-3xl italic leading-relaxed mb-8 ${
          variant === 'light' ? 'text-stone-700' : 'text-stone-200'
        }`}
      >
        {text}
      </blockquote>
      <cite
        className={`font-body text-sm tracking-widest uppercase not-italic ${
          variant === 'light' ? 'text-stone-500' : 'text-stone-400'
        }`}
      >
        — {author}
      </cite>
    </Container>
  </div>
);

// Image placeholder
export const ImagePlaceholder = ({ aspectRatio = 'aspect-[4/5]', label = 'Professional Photo' }) => (
  <div className="relative">
    <div className={`${aspectRatio} bg-gradient-to-br from-stone-200 to-stone-300 rounded-sm overflow-hidden shadow-2xl`}>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-body text-stone-400 text-sm tracking-wide">{label}</span>
      </div>
    </div>
    <div className="absolute -bottom-4 -right-4 w-full h-full border border-amber-300/50 rounded-sm -z-10" />
  </div>
);
